import React, { useState, useEffect } from 'react';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

export default function JournalModal({ serverId, serviceUserId, onClose }) {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchEntries = async () => {
            setLoading(true);
            try {
                const ref = collection(db, 'servers', serverId, 'serviceUsers', serviceUserId, 'journal');
                const snap = await getDocs(query(ref));
                
                const loaded = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                
                loaded.sort((a, b) => {
                    const getMs = (val) => {
                        if (!val) return 0;
                        if (typeof val.toDate === 'function') return val.toDate().getTime();
                        if (val.seconds) return val.seconds * 1000;
                        return new Date(val).getTime() || 0;
                    };
                    const dateA = getMs(a.date || a.timestamp || a.createdAt);
                    const dateB = getMs(b.date || b.timestamp || b.createdAt);
                    return dateB - dateA; // Newest first
                });
                
                setEntries(loaded);
            } catch (err) {
                console.error("Error fetching journal entries:", err);
                setError("Failed to load journal entries.");
            } finally {
                setLoading(false);
            }
        };

        if (serverId && serviceUserId) {
            fetchEntries();
        }
    }, [serverId, serviceUserId]);

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '20px', color: '#111827' }}>Journal</h2>
                        <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '14px' }}>Daily notes, updates, and general journal</p>
                    </div>
                    <button onClick={onClose} style={styles.closeBtn}>×</button>
                </div>

                <div style={styles.scrollArea}>
                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading journal...</div>
                    ) : error ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>{error}</div>
                    ) : entries.length === 0 ? (
                        <div style={styles.emptyState}>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📓</div>
                            <h3 style={{ margin: '0 0 8px 0', color: '#374151' }}>No Entries</h3>
                            <p style={{ margin: 0, color: '#6b7280' }}>There are no journal entries recorded for this service user.</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {entries.map((entry, index) => {
                                const author = entry.author || entry.createdBy || 'Unknown User';
                                const content = entry.notes || entry.content || entry.details || 'No content provided';
                                
                                return (
                                    <div key={entry.id || index} style={styles.recordCard}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 'bold', color: '#4b5563' }}>
                                                    {author.charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#111827' }}>{author}</div>
                                                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                                        {entry.date || entry.timestamp || entry.createdAt ? new Date((entry.date || entry.timestamp || entry.createdAt)?.seconds ? (entry.date || entry.timestamp || entry.createdAt).seconds * 1000 : (entry.date || entry.timestamp || entry.createdAt)).toLocaleString() : 'N/A'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div style={{ fontSize: '14px', color: '#374151', whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>
                                            {content}
                                        </div>
                                        
                                        {entry.tags && entry.tags.length > 0 && (
                                            <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                                                {entry.tags.map((tag, i) => (
                                                    <span key={i} style={{ backgroundColor: '#f3f4f6', color: '#4b5563', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>
                                                        #{tag}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const styles = {
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    modalContent: { backgroundColor: 'white', borderRadius: '12px', width: '90%', maxWidth: '700px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' },
    modalHeader: { padding: '24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
    closeBtn: { background: 'none', border: 'none', fontSize: '24px', color: '#9ca3af', cursor: 'pointer', lineHeight: 1 },
    scrollArea: { padding: '24px', overflowY: 'auto', flex: 1, backgroundColor: '#f9fafb' },
    emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 0', textAlign: 'center', backgroundColor: 'white', borderRadius: '8px', border: '1px dashed #d1d5db' },
    recordCard: { backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }
};
